import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { Panel, SectionTitle } from "../components/Tactical";
import ReliabilityBadge from "../components/ReliabilityBadge";
import PushSettings from "../components/PushSettings";
import { toast } from "sonner";
import { User as UserIcon, Shield, Trash2, AlertTriangle, Camera, Upload, CheckCircle2, LogOut } from "lucide-react";
import { COUNTRIES, flagFor } from "../lib/countries";

function ExitLeaguePanel({ user, refreshMe }) {
  const { t } = useT();
  const [show, setShow] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!user?.league_id) return null; // nothing to exit

  const doExit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/leagues/me/exit", {});
      const n = data.forfeited_matches ?? 0;
      if (data.new_league_id) {
        toast.success(t("exit.toastJoined", { n }));
      } else {
        toast.message(t("exit.toastNoNewLeague", { n }));
      }
      setShow(false); setConfirm("");
      if (refreshMe) await refreshMe();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel testId="exit-league-panel" className="border-l-2 border-l-[#EA580C]">
      <div className="flex items-center gap-2 mb-2">
        <LogOut className="w-4 h-4 text-[#EA580C]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#EA580C]">{t("exit.kicker")}</span>
      </div>
      <h3 className="font-heading text-2xl uppercase mb-2">{t("exit.title")}</h3>
      <p className="font-body text-sm text-[#09090B] dark:text-[#F4F4F5] mb-4 leading-relaxed">{t("exit.body")}</p>
      {!show ? (
        <button
          type="button"
          onClick={() => { setShow(true); setErr(""); }}
          data-testid="exit-league-open"
          className="inline-flex items-center gap-2 border border-[#EA580C] text-[#EA580C] font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 hover:bg-[#EA580C] hover:text-white transition-colors"
        >
          <LogOut className="w-3 h-3" /> {t("exit.button")}
        </button>
      ) : (
        <form onSubmit={doExit} className="space-y-3" data-testid="exit-league-form">
          <div className="font-mono text-[11px] text-[#71717A]">{t("exit.confirmBody")}</div>
          <input
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={user.username}
            required
            autoComplete="off"
            data-testid="exit-league-confirm-input"
            className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#EA580C] outline-none text-[#09090B] dark:text-[#F4F4F5]"
          />
          {err && <div className="font-mono text-[11px] text-[#E11D48]" data-testid="exit-league-error">// {err}</div>}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="submit"
              disabled={busy || confirm.trim().toLowerCase() !== (user.username || "").toLowerCase()}
              data-testid="exit-league-submit"
              className="inline-flex items-center gap-2 bg-[#EA580C] text-white font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 disabled:opacity-40 hover:bg-[#C2410C] transition-colors"
            >
              <LogOut className="w-3 h-3" /> {busy ? t("exit.exiting") : t("exit.confirm")}
            </button>
            <button
              type="button"
              data-testid="exit-league-cancel"
              onClick={() => { setShow(false); setConfirm(""); setErr(""); }}
              className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5] px-3 py-2"
            >
              {t("exit.cancel")}
            </button>
          </div>
        </form>
      )}
    </Panel>
  );
}

function ProfileCompletionPanel({ user, refreshMe }) {
  const { t } = useT();
  const [email, setEmail] = useState(user?.email || "");
  const [nationality, setNationality] = useState(user?.nationality || "");
  const [clubName, setClubName] = useState(user?.club_name || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setEmail(user?.email || "");
    setNationality(user?.nationality || "");
    setClubName(user?.club_name || "");
  }, [user?.email, user?.nationality, user?.club_name]);

  const complete = !!(user?.email && user?.nationality && user?.club_name);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.patch("/users/me/profile", {
        email: email.trim() || null,
        nationality: nationality.trim() || null,
        club_name: clubName.trim() || null,
      });
      toast.success(t("pc.saved"));
      if (refreshMe) await refreshMe();
    } catch (e2) {
      const status = e2?.response?.status;
      const detail = e2?.response?.data?.detail;
      if (status === 409 && detail && /club name/i.test(detail)) {
        setErr(t("pc.clubTakenError"));
      } else {
        setErr(formatApiError(e2));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel testId="profile-completion-panel" className={complete ? "" : "border-l-2 border-l-[#EA580C]"}>
      <div className="flex items-center gap-2 mb-3">
        {complete ? (
          <CheckCircle2 className="w-4 h-4 text-[#00FF87]" />
        ) : (
          <AlertTriangle className="w-4 h-4 text-[#EA580C]" />
        )}
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{t("pc.kicker")}</span>
      </div>
      <h3 className="font-heading text-2xl uppercase mb-1">{t("pc.title")}</h3>
      <p className="text-sm text-[#71717A] mb-4 font-body leading-relaxed">{t("pc.subtitle")}</p>
      <div
        className={`mb-5 font-mono text-[10px] uppercase tracking-[0.25em] ${
          complete ? "text-[#00FF87]" : "text-[#EA580C]"
        }`}
        data-testid="profile-completion-status"
      >
        {complete ? `[ ${t("pc.complete")} ]` : `[ ${t("pc.incomplete")} ]`}
      </div>
      <form onSubmit={save} className="space-y-4" data-testid="profile-completion-form">
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("pc.email")}</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("pc.emailPh")}
            data-testid="pc-email-input"
            className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]"
            dir="ltr"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("pc.nationality")}</label>
          <select
            value={nationality}
            onChange={(e) => setNationality(e.target.value)}
            data-testid="pc-nationality-select"
            className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]"
          >
            <option value="">{t("pc.nationalityPh")}</option>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
          {nationality && (
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
              <span className="text-base mr-1" aria-hidden="true">{flagFor(nationality)}</span>{nationality}
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("pc.clubName")}</label>
          <input
            type="text"
            value={clubName}
            onChange={(e) => setClubName(e.target.value)}
            placeholder={t("pc.clubNamePh")}
            maxLength={64}
            data-testid="pc-club-name-input"
            className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]"
          />
          <div
            className="mt-2 border border-[#EA580C]/40 bg-[#EA580C]/10 px-3 py-2 text-[12px] text-[#EA580C] leading-relaxed font-body"
            data-testid="pc-club-warning"
          >
            <strong className="font-bold">⚠ {t("pc.clubWarning")}</strong>
          </div>
        </div>
        {err && <div className="font-mono text-[11px] text-[#E11D48]" data-testid="pc-error">// {err}</div>}
        <button
          type="submit"
          disabled={busy}
          data-testid="pc-save-btn"
          className="bg-[#7C3AED] text-white px-4 py-2 font-mono text-xs uppercase tracking-widest disabled:opacity-40 hover:bg-[#6D28D9] transition-colors"
        >
          {busy ? t("pc.saving") : t("pc.save")}
        </button>
      </form>
    </Panel>
  );
}

function AvatarUploader({ user, onChange }) {
  const { t } = useT();
  const [avatar, setAvatar] = useState(user?.avatar_url || null);
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);

  useEffect(() => {
    setAvatar(user?.avatar_url || null);
  }, [user?.avatar_url]);

  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error(t("acc.imageTooBig"));
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      const { data } = await api.post("/users/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAvatar(data.avatar_url);
      toast.success(t("acc.picUpdated"));
      if (onChange) onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.delete("/users/me/avatar");
      setAvatar(null);
      toast.success(t("acc.picRemoved"));
      if (onChange) onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-5" data-testid="avatar-uploader">
      <div
        className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-[#0E0E14] to-[#1A1735] ring-2 ring-[#7C3AED]/60 shadow-[0_6px_18px_rgba(0,0,0,0.4)] flex items-center justify-center flex-shrink-0"
        data-testid="avatar-preview"
      >
        {avatar ? (
          <img src={avatar} alt="avatar" className="w-full h-full object-cover" />
        ) : (
          <UserIcon className="w-16 h-16 text-black/80" strokeWidth={2} />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => doUpload(e.target.files?.[0])}
          className="hidden"
          data-testid="avatar-file-input"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="avatar-upload-btn"
          className="inline-flex items-center gap-2 bg-[#7C3AED] text-white font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 disabled:opacity-40 hover:bg-[#6D28D9] transition-colors"
        >
          <Upload className="w-3 h-3" /> {avatar ? t("acc.replacePhoto") : t("acc.uploadPhoto")}
        </button>
        {avatar && (
          <button
            type="button"
            onClick={clear}
            disabled={busy}
            data-testid="avatar-clear-btn"
            className="inline-flex items-center gap-2 border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 hover:border-[#E11D48] hover:text-[#E11D48] transition-colors"
          >
            {t("acc.removeBtn")}
          </button>
        )}
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#71717A]">
          {t("acc.picMeta")}
        </div>
      </div>
    </div>
  );
}

export default function Account() {
  const { user, refreshMe, logout } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [newName, setNewName] = useState(user && user !== false ? user.username : "");
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Reliability self-score
  const [reliability, setReliability] = useState(null);
  useEffect(() => {
    if (!user || user === false) return;
    (async () => {
      try {
        const { data } = await api.get(`/players/${encodeURIComponent(user.username)}/reliability`);
        setReliability(data);
      } catch { /* noop */ }
    })();
  }, [user]);

  // Delete-account state
  const [showDelete, setShowDelete] = useState(false);
  const [delPass, setDelPass] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  if (!user || user === false) return null;

  const changeName = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.patch("/users/me/username", { username: newName.trim() });
      toast.success(t("acc.usernameUpdated", { name: data.username }));
      if (refreshMe) await refreshMe();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const changePass = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/change-password", { old_password: oldPass, new_password: newPass });
      toast.success(t("acc.passwordUpdated"));
      setOldPass(""); setNewPass("");
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const deleteMe = async (e) => {
    e.preventDefault();
    setDeleting(true); setErr("");
    try {
      const { data } = await api.delete("/users/me", {
        data: { password: delPass, confirm: delConfirm },
      });
      toast.success(
        t("acc.deletedToast", {
          n: data.removed_from_leagues,
          slots: data.slots_reopened ? t("acc.slotsReopened", { n: data.slots_reopened }) : "",
          forfeits: data.forfeited_matches ? t("acc.forfeitedMatches", { n: data.forfeited_matches }) : "",
        })
      );
      if (logout) await logout();
      navigate("/", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="account-page" className="space-y-8 max-w-xl">
      <SectionTitle kicker={t("acc.kicker")} testId="account-title">{t("acc.title")}</SectionTitle>

      <div className="flex items-center gap-3 flex-wrap">
        <Link
          to={`/players/${encodeURIComponent(user.username)}`}
          data-testid="account-view-profile"
          className="inline-flex items-center gap-2 border border-[#7C3AED] text-[#7C3AED] font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 hover:bg-[#7C3AED] hover:text-white transition-colors"
        >
          <UserIcon className="w-3 h-3" /> {t("acc.viewProfile")}
        </Link>
        {reliability && (
          <div className="flex items-center gap-2" data-testid="account-reliability">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{t("acc.yourReliability")}</span>
            <ReliabilityBadge score={reliability.score} flagged={reliability.flagged} size="md" testId="account-reliability-badge" />
          </div>
        )}
      </div>

      {reliability && reliability.breakdown && (
        <Panel testId="account-reliability-panel">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-[#7C3AED]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{t("acc.scoreBreakdown")}</span>
          </div>
          <ul className="grid grid-cols-2 gap-2 font-mono text-[11px] text-[#09090B] dark:text-[#F4F4F5]">
            <li>{t("acc.matchesInWindow")}: <span className="text-[#7C3AED]">{reliability.breakdown.matches_in_window ?? 0}</span></li>
            <li>{t("acc.completion")}: <span className="text-[#00FF87]">{Math.round((reliability.breakdown.completion_rate || 0) * 100)}%</span></li>
            <li>{t("acc.onTime")}: <span className="text-[#00FF87]">{Math.round((reliability.breakdown.on_time_rate || 0) * 100)}%</span></li>
            <li>{t("acc.chatEng")}: <span className="text-[#00FF87]">{Math.round((reliability.breakdown.chat_engagement_rate || 0) * 100)}%</span></li>
            <li>{t("acc.scoreAgreement")}: <span className="text-[#00FF87]">{Math.round((reliability.breakdown.agreement_rate || 0) * 100)}%</span></li>
            <li>{t("acc.reports90d")}: <span className={reliability.breakdown.unique_reports ? "text-[#E11D48]" : ""}>{reliability.breakdown.unique_reports ?? 0}</span></li>
            <li>{t("acc.autoForfeits")}: <span className={reliability.breakdown.auto_forfeits ? "text-[#EA580C]" : ""}>{reliability.breakdown.auto_forfeits ?? 0}</span></li>
            {reliability.breakdown.inactivity_days !== null && reliability.breakdown.inactivity_days !== undefined && (
              <li>{t("acc.inactiveDays")}: <span className="text-[#71717A]">{reliability.breakdown.inactivity_days}</span></li>
            )}
          </ul>
        </Panel>
      )}

      <PushSettings />

      <ProfileCompletionPanel user={user} refreshMe={refreshMe} />

      {/* Profile picture upload */}
      <Panel testId="account-avatar-panel">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-4">{t("acc.profilePicture")}</div>
        <AvatarUploader user={user} onChange={() => refreshMe && refreshMe()} />
      </Panel>

      <Panel>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-4">{t("acc.changeUsername")}</div>
        <form onSubmit={changeName} className="space-y-3" data-testid="change-username-form">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A1A1AA]">{t("acc.current")}: <span className="text-[#34C759]">{user.username}</span></div>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid="new-username-input"
            minLength={3}
            maxLength={30}
            required
            className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]"
            placeholder={t("acc.newUsername")}
          />
          <button
            type="submit"
            disabled={busy || !newName.trim() || newName.trim() === user.username}
            data-testid="change-username-submit"
            className="bg-[#7C3AED] text-white px-4 py-2 font-mono text-xs uppercase tracking-widest disabled:opacity-40 hover:bg-[#6D28D9] transition-colors"
          >
            {t("acc.updateUsername")}
          </button>
        </form>
      </Panel>

      <Panel>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-4">{t("acc.changePassword")}</div>
        <form onSubmit={changePass} className="space-y-3" data-testid="change-password-form">
          <input
            type="password"
            value={oldPass}
            onChange={(e) => setOldPass(e.target.value)}
            data-testid="old-password-input"
            required
            className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]"
            placeholder={t("acc.currentPassword")}
          />
          <input
            type="password"
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            data-testid="new-password-input"
            required
            minLength={4}
            className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]"
            placeholder={t("acc.newPassword")}
          />
          <button
            type="submit"
            disabled={busy || !oldPass || newPass.length < 4}
            data-testid="change-password-submit"
            className="bg-[#7C3AED] text-white px-4 py-2 font-mono text-xs uppercase tracking-widest disabled:opacity-40 hover:bg-[#6D28D9] transition-colors"
          >
            {t("acc.updatePassword")}
          </button>
        </form>
      </Panel>

      {err && <div className="border border-[#E11D48] text-[#E11D48] font-mono text-xs p-3" data-testid="account-error">[ {err} ]</div>}

      {/* Exit league — keeps account, doesn't reopen slot */}
      <ExitLeaguePanel user={user} refreshMe={refreshMe} />

      {/* Danger zone — account deletion */}
      <Panel testId="account-danger-zone" className="border-l-2 border-l-[#E11D48]">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-[#E11D48]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E11D48]">{t("acc.dangerZone")}</span>
        </div>
        <div className="font-body text-sm text-[#09090B] dark:text-[#F4F4F5] mb-3 leading-relaxed">
          {t("acc.dangerBody")}
        </div>
        {!showDelete ? (
          <button
            type="button"
            data-testid="account-delete-open"
            onClick={() => { setShowDelete(true); setErr(""); }}
            className="inline-flex items-center gap-2 border border-[#E11D48] text-[#E11D48] font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 hover:bg-[#E11D48] hover:text-white transition-colors"
          >
            <Trash2 className="w-3 h-3" /> {t("acc.deleteMyAccount")}
          </button>
        ) : (
          <form onSubmit={deleteMe} className="space-y-3" data-testid="account-delete-form">
            <input
              type="password"
              value={delPass}
              onChange={(e) => setDelPass(e.target.value)}
              placeholder={t("acc.confirmWithPassword")}
              required
              data-testid="account-delete-password"
              className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#E11D48] outline-none text-[#09090B] dark:text-[#F4F4F5]"
            />
            <input
              type="text"
              value={delConfirm}
              onChange={(e) => setDelConfirm(e.target.value)}
              placeholder={t("acc.typeUsername", { name: user.username })}
              required
              autoComplete="off"
              data-testid="account-delete-confirm"
              className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#E11D48] outline-none text-[#09090B] dark:text-[#F4F4F5]"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="submit"
                disabled={deleting || !delPass || delConfirm.trim().toLowerCase() !== user.username.toLowerCase()}
                data-testid="account-delete-submit"
                className="inline-flex items-center gap-2 bg-[#E11D48] text-white font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 disabled:opacity-40 hover:bg-[#BE123C] transition-colors"
              >
                <Trash2 className="w-3 h-3" /> {deleting ? t("acc.deleting") : t("acc.permanentlyDelete")}
              </button>
              <button
                type="button"
                data-testid="account-delete-cancel"
                onClick={() => { setShowDelete(false); setDelPass(""); setDelConfirm(""); setErr(""); }}
                className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5] px-3 py-2"
              >
                {t("acc.cancel")}
              </button>
            </div>
          </form>
        )}
      </Panel>
    </div>
  );
}
